import { __assign } from "tslib";
import * as React from 'react';
import { InputAdornment, IconButton, TextField } from '@material-ui/core';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import { useField } from 'formik';
import { BLayout } from '../BLayout';
import { BError } from './BError';
/**
 * **Must be a child of BForm**
 *
 * BPassword is configured to work with any password field you have.
 *
 *  **Required Props:**
 *  - name
 *      - Must match the name from initialValues
 *
 * **Optional Props:**
 * - [TextFieldProps](https://material-ui.com/api/text-field/#props)
 *  - almost any Material-UI props
 * - [BLayout](https://gitlab.com/2020-bootcamp/forms/mui-bueno/-/blob/update-docs/documentations/tag-references/MWidget.md)
 *  - custom gridding, sizes, and alignment
 * - [complexity](https://gitlab.com/2020-bootcamp/forms/mui-bueno/-/blob/update-docs/docs/tag-references/input/BPassword.md#other-exports-from-bpasswordtsx)
 *  - custom password requirements
 * - required
 *  - default is false, if true the field will be required.
 * - placeholder
 *  - the short hint displayed in the input before the user enters a value.
 * - label
 *  - the label content.
 *
 * **Basic Example:**
 * ```jsx
 * <BPassword
 *   name="password"
 *   label="Password"
 *   complexity={{
 *     minimum: 5,
 *     maximum: 40,
 *     capital: 1,
 *     number: 1
 *  }}
 * />;
 * ```
 *
 * Demos:
 *
 * - [Sample Form](https://material-ui.com/components/text-fields/)
 *
 * API:
 *
 * - [BPassword API](https://gitlab.com/2020-bootcamp/forms/mui-bueno/-/blob/update-docs/docs/tag-references/input/BPassword.md)
 */
export var BPassword = function (props) {
    var defaultComplexity = {
        minimum: 4,
        maximum: 99,
        capital: 0,
        lowercase: 0,
        number: 0,
        special: 0,
        ascii: true
    };
    var name = props.name, _a = props.label, label = _a === void 0 ? 'Password' : _a, _b = props.placeholder, placeholder = _b === void 0 ? 'Enter password' : _b, _c = props.complexity, complexity = _c === void 0 ? defaultComplexity : _c, _d = props.toggleView, toggleView = _d === void 0 ? true : _d, _e = props.required, required = _e === void 0 ? false : _e, _f = props.w, w = _f === void 0 ? 2 : _f, className = props.className;
    //Used to update password visibility
    var _g = React.useState(false), visible = _g[0], setVisible = _g[1];
    // Formik boilerplate ------------------
    var _h = useField({ name: name, validate: function () { return validateComplexity(); } }), field = _h[0], meta = _h[1];
    var value = field.value;
    var error = meta.error, touched = meta.touched;
    // -------------------------------------
    if (value === undefined) {
        console.error("Please set an initial value for " + name);
    }
    var containsInRange = function (str, target, rangeMin, rangeMax) {
        var count = 0;
        for (var i = 0; i < str.length; i++) {
            var c = str.charCodeAt(i);
            if (c >= rangeMin && c <= rangeMax) {
                count++;
                if (count >= target) {
                    return true;
                }
            }
        }
        return false;
    };
    var validateComplexity = function () {
        var myErrors = [];
        if (!value) {
            return required ? label + " is required" : '';
        }
        if (complexity.minimum && value.length < complexity.minimum) {
            myErrors.push("Password is less than " + complexity.minimum + " characters. ");
        }
        if (complexity.maximum && value.length > complexity.maximum) {
            myErrors.push("Password is more than " + complexity.maximum + " characters. ");
        }
        if (complexity.capital && !containsInRange(value, complexity.capital, 'A'.charCodeAt(0), 'Z'.charCodeAt(0))) {
            myErrors.push("Password has less than the required " + complexity.capital + " capital letter(s). ");
        }
        if (complexity.lowercase && !containsInRange(value, complexity.lowercase, 'a'.charCodeAt(0), 'z'.charCodeAt(0))) {
            myErrors.push("Password has less than the required " + complexity.lowercase + " lowercase letter(s). ");
        }
        if (complexity.number && !containsInRange(value, complexity.number, '0'.charCodeAt(0), '9'.charCodeAt(0))) {
            myErrors.push("Password has less than the required " + complexity.number + " number(s).");
        }
        if (complexity.special && complexity.special > 0) {
            var count = 0;
            var special = '!"#$%&\'()*+-=?@[]^_{|}~';
            for (var i = 0; i < value.length; i++) {
                if (special.includes(value[i])) {
                    count++;
                    if (count >= complexity.special) {
                        break;
                    }
                }
            }
            if (count < complexity.special) {
                myErrors.push("Password has less than the required " + complexity.special + " special character(s). ");
            }
        }
        if (complexity.ascii && !containsInRange(value, value.length, 0, 127)) {
            myErrors.push('Password can only include ASCII characters.');
        }
        if (myErrors.length > 0) {
            return myErrors[0];
        }
        else {
            return '';
        }
    };
    var toggleVisibility = function () {
        setVisible(!visible);
    };
    var blockMouseDown = function (event) {
        event.preventDefault();
        setVisible(!visible);
    };
    return (React.createElement(BLayout, __assign({}, props, { w: w }),
        React.createElement("div", { className: className },
            React.createElement(TextField, __assign({ margin: 'dense', variant: 'outlined' }, field, props.textFieldProps, { id: name + "-pw", label: label, type: visible ? 'text' : 'password', fullWidth: true, placeholder: placeholder, error: Boolean(error) && touched, InputProps: toggleView ? {
                    endAdornment: React.createElement(InputAdornment, { position: 'end' },
                        React.createElement(IconButton, { id: props.name, onClick: toggleVisibility, onMouseDown: blockMouseDown }, visible ? React.createElement(Visibility, null) : React.createElement(VisibilityOff, null)))
                } : {} })),
            React.createElement(BError, { name: name, id: name + "-err" }))));
};
//# sourceMappingURL=BPassword.js.map